import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from 'lucide-react';
import { uploadDenialDocument, getDenialAnalysis } from '../../../utils/denialAnalysisApi';

const OCR_STATUS_LABELS = {
  pending: { label: 'Waiting', color: 'default' },
  success: { label: 'OCR Done', color: 'success' },
  failed: { label: 'OCR Failed', color: 'error' },
};

const UploadStep = ({ analysis, onNext, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error, setError] = useState(null);
  const [ocrReady, setOcrReady] = useState(false);
  const [extractedFields, setExtractedFields] = useState(null);
  const pollRef = useRef(null);

  // Start polling when a file is uploaded
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const response = await getDenialAnalysis(analysis.id);
        const updatedAnalysis = response.data;
        onUpdate(updatedAnalysis);

        const docs = updatedAnalysis.documents || [];
        const allDone = docs.length > 0 && docs.every(d => d.ocr_status !== 'pending');
        const anySuccess = docs.some(d => d.ocr_status === 'success');

        setUploadedFiles(docs);

        if (allDone) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (anySuccess) {
            setOcrReady(true);
            mergeExtractedFields(docs);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  }, [analysis?.id, onUpdate]);

  const mergeExtractedFields = useCallback((docs) => {
    const merged = {};
    docs.forEach(doc => {
      const data = doc.extraction_metadata?.extracted_data || {};
      Object.keys(data).forEach(key => {
        if (data[key] && !merged[key]) merged[key] = data[key];
      });
    });
    if (Object.keys(merged).length > 0) setExtractedFields(merged);
  }, []);

  useEffect(() => {
    // Initialize from existing analysis documents
    if (analysis?.documents?.length > 0) {
      setUploadedFiles(analysis.documents);
      const anySuccess = analysis.documents.some(d => d.ocr_status === 'success');
      const allDone = analysis.documents.every(d => d.ocr_status !== 'pending');
      if (anySuccess && allDone) {
        setOcrReady(true);
        mergeExtractedFields(analysis.documents);
      } else if (!allDone) {
        startPolling();
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [analysis?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    const file = acceptedFiles[0];

    try {
      setUploading(true);
      setError(null);
      await uploadDenialDocument(analysis.id, file);
      setUploadedFiles(prev => [...prev, { original_filename: file.name, ocr_status: 'pending' }]);
      startPolling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [analysis?.id, startPolling]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg', '.tiff'] },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Denial Letter
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a PDF or image of the denial letter. OCR will extract the text automatically.
      </Typography>

      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          bgcolor: isDragActive ? 'primary.50' : 'background.default',
          mb: 2,
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon size={48} />
        <Typography>
          {isDragActive ? 'Drop the file here' : 'Drag & drop a denial letter, or click to select'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PDF, PNG, JPG, TIFF supported
        </Typography>
      </Paper>

      {uploading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {uploadedFiles.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Uploaded Files</Typography>
          <List dense>
            {uploadedFiles.map((doc, idx) => {
              const statusInfo = OCR_STATUS_LABELS[doc.ocr_status] || OCR_STATUS_LABELS.pending;
              return (
                <ListItem key={doc.id || idx} sx={{ pl: 0 }}>
                  <ListItemText primary={doc.original_filename} />
                  <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                </ListItem>
              );
            })}
          </List>
          {!ocrReady && uploadedFiles.some(d => d.ocr_status === 'pending') && (
            <Typography variant="caption" color="text.secondary">
              Extracting text from document...
            </Typography>
          )}
        </Box>
      )}

      {extractedFields && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Extracted Information
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Key fields extracted from your document. Review before continuing.
          </Typography>
          <Grid container spacing={2}>
            {[
              { key: 'patient_name', label: 'Patient Name' },
              { key: 'payer_name', label: 'Payer / Insurance' },
              { key: 'claim_number', label: 'Claim Number' },
              { key: 'denial_code', label: 'Denial Code' },
            ].map(({ key, label }) =>
              extractedFields[key] ? (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={extractedFields[key]}
                    variant="filled"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              ) : null
            )}
            {extractedFields.denial_text && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Denial Reason"
                  value={extractedFields.denial_text}
                  variant="filled"
                  multiline
                  rows={2}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={onNext}
        disabled={!ocrReady}
        fullWidth
      >
        Continue to Analysis
      </Button>
    </Box>
  );
};

export default UploadStep;
